import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

type Run = { taskId: string; agentId: string; agentName?: string; state: string; createdAt: string; traceId?: string; source?: string; domain?: string | null; duration?: number; modelName?: string; governance?: string; outcome?: string; controls?: { allowedTools?: string[]; toolCapabilities?: Array<{name:string; available:boolean; metadata?: {effect:string; variants?:Record<string,string>} | null}>; checks?: Array<{id: string; pass: boolean; detail: string}> }; events?: Array<{type: string; at?: string}> };
type Agent = { id: string; name: string; description?: string; source?: string; domain?: string; status?: string };
type LoadState = 'loading' | 'ready' | 'unavailable';

export default function HarnessConsole() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [catalogState, setCatalogState] = useState<LoadState>('loading');
  const [runsState, setRunsState] = useState<LoadState>('loading');
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyRun, setBusyRun] = useState<string | null>(null);
  const actOnRun = async (r: Run, action: 'approve' | 'reject') => {
    if (action === 'approve' && !window.confirm('Approve this agent execution? The agent may perform external actions.')) return;
    setBusyRun(r.taskId); setActionError(null);
    try {
      const approvedActions = action === 'approve' ? (r.controls?.toolCapabilities || []).flatMap(item => {
        if (!item.available || !item.metadata) return [];
        const write = item.metadata.effect === 'write' || (item.metadata.effect === 'conditional' && Object.values(item.metadata.variants || {}).includes('write'));
        return write && window.confirm('Authorize WRITE capability for this run? ' + item.name + ':write') ? [item.name + ':write'] : [];
      }) : [];
      await api.post('/api/harness/runs/' + encodeURIComponent(r.taskId) + '/' + action, action === 'approve' ? { approvedActions } : {});
      await refresh();
    }
    catch (e: any) { setActionError(e?.response?.data?.error || 'Unable to update approval'); }
    finally { setBusyRun(null); }
  };

  const refresh = useCallback(async () => {
    setCatalogState('loading');
    setRunsState('loading');
    // Independent requests: one failing endpoint must not hide the other section.
    const [catalog, history] = await Promise.allSettled([
      api.get('/api/harness/agents'),
      api.get('/api/harness/runs')
    ]);
    if (catalog.status === 'fulfilled') {
      setAgents(Array.isArray(catalog.value.data?.agents) ? catalog.value.data.agents : []);
      setCatalogState('ready');
    } else {
      setCatalogState('unavailable');
    }
    if (history.status === 'fulfilled') {
      setRuns(Array.isArray(history.value.data?.runs) ? history.value.data.runs : []);
      setRunsState('ready');
    } else {
      setRunsState('unavailable');
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const agentLabel = (r: Run) => {
    const matched = agents.find(a => a.id === r.agentId || a.id.endsWith(':' + r.agentId));
    const raw = String(r.agentName || '').trim();
    const technical = !raw || /^[a-f0-9]{18,}$/i.test(raw) || raw === r.agentId;
    if (!technical) return raw;
    if (matched?.name) return matched.name;
    if (r.domain) return r.domain.charAt(0).toUpperCase() + r.domain.slice(1) + ' agent';
    if (r.source === 'agent-run') return 'Personal assistant / agent';
    return 'Agent execution';
  };
  const statusLabel = (state: string) => state === 'completed' ? 'Finished' : state === 'failed' ? 'Failed' : state === 'running' ? 'Running' : state === 'queued' ? 'Queued' : state;
  const panel: React.CSSProperties = {
    background: 'var(--app-surface, #fff)',
    border: '1px solid var(--app-border, #e5e7eb)',
    borderRadius: 14,
    padding: 22,
    minWidth: 0
  };
  const muted: React.CSSProperties = { color: 'var(--app-text-2, #64748b)', fontSize: 13 };
  const stateText = (state: LoadState, empty: string) =>
    state === 'loading' ? 'Loading…' : state === 'unavailable'
      ? 'This endpoint is unavailable. Check the Lyra Server deployment and API routing.'
      : empty;

  return (
    <main style={{ maxWidth: 1180, margin: '0 auto', padding: '20px clamp(12px, 3vw, 28px) 48px', color: 'var(--app-text, #172033)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <div style={{ ...muted, marginBottom: 6 }}>Advanced / Agent Harness</div>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Agent Harness</h1>
          <p style={{ ...muted, marginTop: 8 }}>Connected domain agents, personal agents and execution history.</p>
        </div>
        <button type="button" onClick={() => void refresh()} style={{ padding: '10px 18px', borderRadius: 9, border: '1px solid var(--app-border, #d1d5db)', background: 'var(--app-surface, #fff)', cursor: 'pointer' }}>Refresh</button>
      </header>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 18 }}>
        <section style={panel} aria-label="Native agents">
          <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>Connected domain agents &amp; my agents</h2>
          <p style={{ ...muted, margin: '0 0 18px' }}>Domain connections appear first, followed by personal and other agents</p>
          {catalogState !== 'ready' || agents.length === 0
            ? <p role={catalogState === 'unavailable' ? 'alert' : undefined} style={muted}>{stateText(catalogState, 'No connected or personal agents found.')}</p>
            : <div style={{ display: 'grid', gap: 10 }}>{agents.map(a =>
                <div key={a.id} style={{ padding: '12px 14px', border: '1px solid var(--app-border, #e5e7eb)', borderRadius: 10 }}>
                  <strong style={{ fontSize: 14 }}>{a.name || a.id}</strong><span style={{ ...muted, marginLeft: 8 }}>{a.domain || a.source || 'native'} · {a.status || 'available'}</span>
                  {a.description && <p style={{ ...muted, margin: '5px 0 0' }}>{a.description}</p>}
                </div>
              )}</div>}
        </section>
        <section style={panel} aria-label="Recent runs">
          <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>Recent runs</h2>
          <p style={{ ...muted, margin: '0 0 18px' }}>Recent chat, domain and harness executions</p>
          {actionError && <p role="alert" style={{ color: 'var(--lyra-color-danger, #b91c1c)' }}>{actionError}</p>}
          {runsState !== 'ready' || runs.length === 0
            ? <p role={runsState === 'unavailable' ? 'alert' : undefined} style={muted}>{stateText(runsState, 'No recorded agent executions yet.')}</p>
            : <div style={{ display: 'grid', gap: 9 }}>
                {runs.map(r => {
                  const expanded = expandedRun === r.taskId;
                  const failed = ['failed', 'error'].includes(r.state);
                  return <article key={r.taskId} style={{ border: '1px solid var(--app-border, #e5e7eb)', borderRadius: 11, padding: '12px 14px', minWidth: 0 }}>
                    <button type="button" aria-expanded={expanded} onClick={() => setExpandedRun(expanded ? null : r.taskId)}
                      style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, width: '100%', textAlign: 'left', background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'inherit' }}>
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <strong style={{ display: 'block', fontSize: 14, overflowWrap: 'anywhere' }}>{agentLabel(r)}</strong>
                        <span style={{ ...muted, display: 'block', marginTop: 4 }}>{r.source === 'harness' ? 'Harness execution' : r.domain ? r.domain + ' domain execution' : 'Agent activity'} · {r.createdAt ? new Date(r.createdAt).toLocaleString() : 'Time unavailable'}</span>
                      </span>
                      <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 8, background: failed ? 'var(--lyra-color-danger-bg, #fee2e2)' : 'var(--app-surface-3, #f1f5f9)', color: failed ? 'var(--lyra-color-danger, #b91c1c)' : 'var(--app-text, #172033)' }}>{statusLabel(r.state)} {expanded ? '⌃' : '⌄'}</span>
                    </button>
                    {expanded && <div style={{ borderTop: '1px solid var(--app-border, #e5e7eb)', marginTop: 12, paddingTop: 10, display: 'grid', gap: 5, ...muted, overflowWrap: 'anywhere' }}>
                      <div><strong>Governance:</strong> {r.governance === 'enforced' ? 'Harness-managed' : 'Legacy execution (Harness controls not enforced)'}</div>
                      <div><strong>Outcome:</strong> {r.outcome === 'output_checks_passed' ? 'Basic output checks passed; business outcome not verified' : 'Not independently verified'}</div>
                      {r.controls?.allowedTools && <div><strong>Available tools:</strong> {r.controls.allowedTools.join(', ') || 'None'} (unregistered capabilities are blocked; writes require separate approval)</div>}
                      {r.controls?.checks?.map(c => <div key={c.id}><strong>{c.pass ? '✓' : '✕'} {c.id}:</strong> {c.detail}</div>)}
                      {r.events?.map((ev, i) => <div key={i}>{ev.type.replace(/_/g, ' ')} {ev.at ? '· ' + new Date(ev.at).toLocaleString() : ''}</div>)}
                      <div><strong>Status:</strong> {r.state === 'completed' ? 'Execution finished; outcome not independently verified here.' : statusLabel(r.state)}</div>
                      {typeof r.duration === 'number' && <div><strong>Duration:</strong> {(r.duration / 1000).toFixed(1)}s</div>}
                      {r.modelName && <div><strong>Model:</strong> {r.modelName}</div>}
                      <div><strong>Agent ID:</strong> {r.agentId || 'Unavailable'}</div>
                      <div><strong>Run ID:</strong> {r.taskId}</div>
                      {r.traceId && <div><strong>Trace ID:</strong> {r.traceId}</div>}
                      {r.state === 'requires_action' && r.governance === 'enforced' && <div style={{ display: 'flex', gap: 8, marginTop: 8 }}><button type="button" disabled={busyRun === r.taskId} onClick={() => void actOnRun(r, 'approve')}>Approve execution</button><button type="button" disabled={busyRun === r.taskId} onClick={() => void actOnRun(r, 'reject')}>Reject</button></div>}
                      <div>Tool-level enforcement and verified business outcomes are not yet available for legacy executions.</div>
                    </div>}
                  </article>;
                })}
              </div>}
        </section>
      </div>
      <p style={{ ...muted, marginTop: 22 }}>Run history includes existing agent records. Nested tool traces, approval enforcement and recovery controls are not yet available here.</p>
    </main>
  );
}
